/**
 * „Altes Angebot übernehmen“ (Antoine, 29.09.2026): früher eine vorgeschaltete Abfrage vor dem
 * Builder (`AngebotUebernahmeDialog`, nur gesendete Angebote, alles auf einmal), jetzt ein Fenster
 * IM Builder — Suche über alle Angebote des Betriebs, Vorschau einer Variante, dann „als neue
 * Variante hinzufügen“ oder „aktuelle Variante ersetzen“ (nach Rückfrage, mit Rückgängig).
 *
 * Alles Fachliche rechnet der Server (`GET /api/angebote/suche`, `GET /api/events/:id/altangebot/
 * :quelle`, `POST /api/events/:id/altangebot-uebernehmen`): Preise aus dem Katalog, Stückzahlen
 * aufgerundet. Hier wird nur angezeigt und gewählt — auch „alten Preis behalten“ schickt keine
 * Beträge, nur die Zeilen-IDs.
 */
import { useEffect, useMemo, useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { useDebounced } from "../hooks/useDebounced";
import { milliToQty } from "../lib/builder-helpers";
import { ANGEBOT_STATUS, type AngebotStatus } from "../pages/angebote";
import {
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  LoadingRows,
  StatusPill,
  useBestaetigung,
} from "./components";
import { anlassAnzeige, formatDate, money, PILL_CLASSES } from "./format";

interface AltangebotTreffer {
  eventId: string;
  number: string | null;
  kundeName: string | null;
  kundeFirma: string | null;
  anlass: string | null;
  veranstaltungsdatum: string | null;
  status: string;
  gaeste: number;
  betragCents: number | null;
  preisProGastCents: number | null;
  angebotStatus: AngebotStatus;
}

interface VorschauZeile {
  id: string;
  section: string;
  courseLabel: string | null;
  name: string;
  description: string | null;
  priceMode: "per_person" | "per_unit" | "flat";
  unit: string | null;
  isOptional: boolean;
  alteMengeMilli: number;
  qtyMilli: number;
  unitPriceCents: number;
  alterPreisCents: number;
  preisAbweichung: boolean;
}

interface Vorschau {
  quelle: { eventId: string; number: string | null; anlass: string | null };
  varianten: {
    optionId: string;
    label: string;
    title: string | null;
    guestCount: number;
    zielGaeste: number;
    items: VorschauZeile[];
  }[];
}

export interface AltangebotErgebnis {
  modus: "neu" | "ersetzen";
  option: { id: string };
  undoId: string | null;
  quellNummer: string;
}

const EINHEIT: Record<VorschauZeile["priceMode"], string> = {
  per_person: "je Gast",
  per_unit: "Stück",
  flat: "pauschal",
};

export function AltesAngebotDrawer({
  open,
  onClose,
  eventId,
  zielOptionId,
  zielLabel,
  gesperrt,
  sektionsLabel,
  vorUebernahme,
  onUebernommen,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Die aktuell bearbeitete Variante (Ziel beim Ersetzen) — `null` im leeren Builder. */
  zielOptionId: string | null;
  zielLabel: string | null;
  /** Nach verbindlicher Zusage: keine Übernahme, derselbe Hinweis wie bei anderen Änderungen. */
  gesperrt: boolean;
  sektionsLabel: Record<string, string>;
  /** Vor dem Schreiben: ausstehende Änderungen der aktuellen Variante sichern. */
  vorUebernahme?: () => Promise<unknown>;
  onUebernommen: (e: AltangebotErgebnis) => void;
}) {
  const [suche, setSuche] = useState("");
  const [seite, setSeite] = useState(0);
  const q = useDebounced(suche.trim(), 300);
  const [quelle, setQuelle] = useState<AltangebotTreffer | null>(null);
  const [variante, setVariante] = useState<string | null>(null);
  const [behalten, setBehalten] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const { bestaetigen, dialog } = useBestaetigung();

  useEffect(() => setSeite(0), [q]);
  useEffect(() => {
    if (open) return;
    setQuelle(null);
    setFehler(null);
  }, [open]);

  const trefferQ = useApi<AltangebotTreffer[], { gesamt: number; seitenGroesse: number }>(
    open && !quelle
      ? `/api/angebote/suche?q=${encodeURIComponent(q)}&seite=${seite}&ausser=${eventId}`
      : null,
  );
  const vorschauQ = useApi<Vorschau>(
    open && quelle
      ? `/api/events/${eventId}/altangebot/${quelle.eventId}${zielOptionId ? `?ziel=${zielOptionId}` : ""}`
      : null,
  );
  const varianten = vorschauQ.data?.varianten ?? [];
  const gewaehlt =
    varianten.find((v) => v.optionId === variante) ?? varianten[0] ?? null;
  const abweichungen =
    gewaehlt?.items.filter((i) => i.preisAbweichung).length ?? 0;

  // Gruppen in der Reihenfolge des Angebots: Gang, sonst Bereich.
  const gruppen = useMemo(() => {
    const m = new Map<string, VorschauZeile[]>();
    for (const it of gewaehlt?.items ?? []) {
      const k = it.courseLabel || sektionsLabel[it.section] || it.section;
      m.set(k, [...(m.get(k) ?? []), it]);
    }
    return [...m.entries()];
  }, [gewaehlt, sektionsLabel]);

  function waehle(t: AltangebotTreffer) {
    setQuelle(t);
    setVariante(null);
    setBehalten(new Set());
    setFehler(null);
  }

  async function uebernehmen(modus: "neu" | "ersetzen") {
    if (!gewaehlt || busy) return;
    if (modus === "ersetzen") {
      const ok = await bestaetigen({
        titel: `Variante ${zielLabel ?? ""} ersetzen?`.replace("  ", " "),
        text: `Alle Positionen der aktuellen Variante werden durch das Angebot aus ${vorschauQ.data?.quelle.number ?? "dem alten Vorgang"} ersetzt.\nDas alte Angebot bleibt unverändert. Du kannst das direkt danach rückgängig machen.`,
        bestaetigen: "Ersetzen",
      });
      if (!ok) return;
    }
    setBusy(true);
    setFehler(null);
    try {
      await vorUebernahme?.();
      const res = await apiMutate<Omit<AltangebotErgebnis, "modus">>(
        `/api/events/${eventId}/altangebot-uebernehmen`,
        "POST",
        {
          quellOptionId: gewaehlt.optionId,
          modus,
          zielOptionId,
          altenPreisBehalten: [...behalten],
        },
      );
      onUebernommen({ ...res, modus });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const gesamt = trefferQ.zusatz?.gesamt ?? 0;
  const groesse = trefferQ.zusatz?.seitenGroesse ?? 20;
  const treffer = trefferQ.data ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      autosave
      breite="breit"
      title="Altes Angebot übernehmen"
      subtitle="Gänge und Positionen werden kopiert – das alte Angebot bleibt unverändert. Kunde, Wünsche und Notizen des alten Vorgangs kommen nicht mit."
      footer={
        quelle && gewaehlt ? (
          <>
            {zielOptionId && (
              <Button
                type="button"
                variant="secondary"
                disabled={busy || gesperrt}
                onClick={() => void uebernehmen("ersetzen")}
                data-testid="altangebot-ersetzen"
              >
                <Icon name="swap_horiz" className="text-[18px]" /> Variante{" "}
                {zielLabel} ersetzen
              </Button>
            )}
            <Button
              type="button"
              disabled={busy || gesperrt}
              onClick={() => void uebernehmen("neu")}
              data-testid="altangebot-neu"
            >
              <Icon name="add" className="text-[18px]" />{" "}
              {busy ? "Wird übernommen …" : "Als neue Variante hinzufügen"}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {gesperrt && (
          <ErrorNote message="Das Angebot wurde verbindlich angenommen – Änderungen sind nur als neue Version mit erneuter Zustimmung möglich." />
        )}
        {fehler && <ErrorNote message={fehler} />}

        {!quelle ? (
          <>
            <div className="group relative">
              <Icon
                name="search"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
              />
              <Input
                className="!pl-10"
                placeholder="Kunde, Anlass, Datum, Gericht, V-Nr. …"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                aria-label="Angebote durchsuchen"
                autoFocus
                data-testid="altangebot-suche"
              />
            </div>
            <div className="-mx-3">
              {trefferQ.loading && treffer.length === 0 ? (
                <LoadingRows rows={4} />
              ) : trefferQ.error ? (
                <ErrorNote message={trefferQ.error} />
              ) : treffer.length === 0 ? (
                <EmptyState
                  icon="content_copy"
                  title={q ? "Nichts gefunden" : "Noch keine anderen Angebote"}
                  hint={
                    q
                      ? "Suche anpassen – gesucht wird in Kunde, Firma, Anlass, Datum, Gästezahl, Positionen und Vorgangsnummer."
                      : "Sobald es weitere Angebote gibt, lassen sie sich hier übernehmen."
                  }
                />
              ) : (
                <ul
                  className="divide-y divide-surface-variant/60 dark:divide-outline/20"
                  data-testid="altangebot-treffer"
                >
                  {treffer.map((t) => (
                    <li key={t.eventId}>
                      <button
                        type="button"
                        onClick={() => waehle(t)}
                        className="flex w-full items-start justify-between gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-surface-container-low dark:hover:bg-white/[0.04]"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-body-md text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                            {anlassAnzeige(t.anlass) || "Ohne Anlass"}
                          </span>
                          <span className="mt-0.5 block truncate font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                            {[t.kundeName, t.kundeFirma]
                              .filter(Boolean)
                              .join(" · ") || "Ohne Kunde"}
                          </span>
                          <span className="mt-0.5 block font-body-sm text-[11.5px] tabular-nums text-secondary-fixed-dim">
                            {t.number ? `${t.number} · ` : ""}
                            {t.veranstaltungsdatum
                              ? formatDate(t.veranstaltungsdatum)
                              : "Ohne Termin"}{" "}
                            · {t.gaeste} Gäste
                            {t.preisProGastCents != null
                              ? ` · ${money(t.preisProGastCents)} pro Gast`
                              : ""}
                          </span>
                        </span>
                        <span className="shrink-0">
                          <StatusPill meta={ANGEBOT_STATUS[t.angebotStatus]} />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {gesamt > groesse && (
              <div className="flex items-center justify-between gap-2 font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                <span className="tabular-nums">
                  {seite * groesse + 1}–
                  {Math.min(gesamt, (seite + 1) * groesse)} von {gesamt}
                </span>
                <span className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={seite === 0}
                    onClick={() => setSeite((s) => s - 1)}
                    aria-label="Vorige Seite"
                  >
                    <Icon name="chevron_left" className="text-[18px]" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={(seite + 1) * groesse >= gesamt}
                    onClick={() => setSeite((s) => s + 1)}
                    aria-label="Nächste Seite"
                  >
                    <Icon name="chevron_right" className="text-[18px]" />
                  </Button>
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4" data-testid="altangebot-vorschau">
            <button
              type="button"
              onClick={() => setQuelle(null)}
              className="inline-flex items-center gap-1 font-body-sm text-[12.5px] font-semibold text-primary hover:underline"
            >
              <Icon name="arrow_back" className="text-[16px]" /> Zurück zur
              Suche
            </button>
            <div className="min-w-0">
              <p className="truncate font-body-md text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">
                {anlassAnzeige(quelle.anlass) || "Ohne Anlass"}
                {quelle.number ? ` · ${quelle.number}` : ""}
              </p>
              <p className="font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                {[quelle.kundeName, quelle.kundeFirma]
                  .filter(Boolean)
                  .join(" · ") || "Ohne Kunde"}{" "}
                ·{" "}
                {quelle.veranstaltungsdatum
                  ? formatDate(quelle.veranstaltungsdatum)
                  : "Ohne Termin"}
              </p>
            </div>

            {vorschauQ.loading && !vorschauQ.data ? (
              <LoadingRows rows={5} />
            ) : vorschauQ.error ? (
              <ErrorNote message={vorschauQ.error} />
            ) : gewaehlt ? (
              <>
                {varianten.length > 1 && (
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="tablist"
                    aria-label="Variante wählen"
                  >
                    {varianten.map((v) => {
                      const aktiv = v.optionId === gewaehlt.optionId;
                      return (
                        <button
                          key={v.optionId}
                          type="button"
                          role="tab"
                          aria-selected={aktiv}
                          onClick={() => {
                            setVariante(v.optionId);
                            setBehalten(new Set());
                          }}
                          className={`rounded-full border px-3 py-1 font-label-md text-[12px] transition-colors ${
                            aktiv
                              ? "border-primary/50 bg-primary/[0.07] text-primary"
                              : "border-outline-variant/60 text-on-surface-variant hover:border-primary/40 dark:border-outline/40"
                          }`}
                        >
                          Variante {v.label}
                          {v.title ? ` · ${v.title}` : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="rounded-lg bg-surface-container-low px-3 py-2 font-body-sm text-[12px] text-on-surface-variant dark:bg-white/[0.04] dark:text-secondary-fixed-dim">
                  Damals {gewaehlt.guestCount} Gäste, jetzt{" "}
                  {gewaehlt.zielGaeste}. Stückzahlen sind auf{" "}
                  {gewaehlt.zielGaeste} Gäste umgerechnet (aufgerundet), Preise
                  kommen aus dem aktuellen Katalog.
                  {abweichungen > 0
                    ? ` ${abweichungen} ${abweichungen === 1 ? "Preis weicht" : "Preise weichen"} vom alten Angebot ab.`
                    : ""}
                </p>
                <div className="space-y-3">
                  {gruppen.map(([titel, zeilen]) => (
                    <div key={titel}>
                      <p className="mb-1 font-label-md text-[11px] font-semibold uppercase tracking-wide text-secondary-fixed-dim">
                        {titel}
                      </p>
                      <ul className="divide-y divide-surface-variant/60 rounded-lg border border-outline-variant/40 dark:divide-outline/20 dark:border-outline/30">
                        {zeilen.map((it) => (
                          <li
                            key={it.id}
                            className="px-3 py-2.5"
                            data-testid={`altangebot-zeile-${it.name}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="break-words font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface">
                                  {it.name}
                                  {it.isOptional && (
                                    <span className="ml-1.5 text-[11px] text-secondary-fixed-dim">
                                      optional
                                    </span>
                                  )}
                                </p>
                                <p className="font-body-sm text-[11.5px] tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">
                                  {milliToQty(it.qtyMilli)}{" "}
                                  {it.priceMode === "per_unit"
                                    ? it.unit || "Stück"
                                    : EINHEIT[it.priceMode]}
                                  {it.qtyMilli !== it.alteMengeMilli
                                    ? ` (damals ${milliToQty(it.alteMengeMilli)})`
                                    : ""}
                                </p>
                              </div>
                              <p className="shrink-0 font-body-sm text-[13px] tabular-nums text-on-background dark:text-inverse-on-surface">
                                {money(
                                  it.preisAbweichung && behalten.has(it.id)
                                    ? it.alterPreisCents
                                    : it.unitPriceCents,
                                )}
                              </p>
                            </div>
                            {it.preisAbweichung && (
                              <div
                                className={`mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md px-2 py-1.5 font-body-sm text-[11.5px] ${PILL_CLASSES.warn}`}
                                data-testid="altangebot-preisabweichung"
                              >
                                <span className="inline-flex items-center gap-1">
                                  <Icon name="sell" className="text-[14px]" />{" "}
                                  Katalog heute {money(it.unitPriceCents)},
                                  damals {money(it.alterPreisCents)}
                                </span>
                                <label className="inline-flex cursor-pointer items-center gap-1.5">
                                  <Checkbox
                                    checked={behalten.has(it.id)}
                                    onChange={(e) =>
                                      setBehalten((alt) => {
                                        const neu = new Set(alt);
                                        if (e.target.checked) neu.add(it.id);
                                        else neu.delete(it.id);
                                        return neu;
                                      })
                                    }
                                  />
                                  alten Preis behalten
                                </label>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
      {dialog}
    </Drawer>
  );
}
